import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../api/useApi';

export type QueueTab = 'all' | 'unassigned' | 'my-queue' | 'on-hold' | 'rejected';

export interface QueueAssignee {
  id: string;
  name: string;
  initials: string;
}

export interface QueueItem {
  id: string;
  orderNumber: string;
  typeLabel: string;
  type: string;
  account: string;
  accountType: 'concierge' | 'direct';
  caseTitle: string;
  caseNumber: string;
  priority: string;
  priorityOrder: number;
  slaMinutesLeft: number;
  slaBreached: boolean;
  slaAtRisk: boolean;
  status: string;
  rawStatus: string;
  isOnHold: boolean;
  assignee: QueueAssignee | null;
  createdAt: string;
}

export interface TabCounts {
  all: number;
  unassigned: number;
  myQueue: number;
  onHold: number;
  rejected: number;
}

export interface QueueKpis {
  inQueue: number;
  urgentCount: number;
  slaBreachRisk: number;
  assignedToMe: number;
}

interface QueueResponse {
  items: QueueItem[];
  pagination: { page: number; per_page: number; total: number };
  tabCounts: TabCounts;
  kpis: QueueKpis;
}

interface UseFilingQueueOptions {
  tab: QueueTab;
  priority?: string;
  search?: string;
  page: number;
  perPage: number;
}

export function useFilingQueue({ tab, priority, search, page, perPage }: UseFilingQueueOptions) {
  const { get, post, isAuthenticated } = useApi();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [tabCounts, setTabCounts] = useState<TabCounts>({ all: 0, unassigned: 0, myQueue: 0, onHold: 0, rejected: 0 });
  const [kpis, setKpis] = useState<QueueKpis>({ inQueue: 0, urgentCount: 0, slaBreachRisk: 0, assignedToMe: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tab, page: String(page), per_page: String(perPage) });
      if (priority) params.set('priority', priority);
      if (search) params.set('search', search);

      const data = await get<QueueResponse>(`/api/admin/queue?${params}`);
      setItems(data.items);
      setTotal(data.pagination.total);
      setTabCounts(data.tabCounts);
      setKpis(data.kpis);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queue');
    } finally {
      setIsLoading(false);
    }
  }, [get, tab, priority, search, page, perPage]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchQueue();
  }, [fetchQueue, isAuthenticated]);

  const assignOrder = useCallback(async (orderId: string, operatorId: string) => {
    await post(`/api/admin/queue/${orderId}/assign`, { operatorId });
    await fetchQueue();
  }, [post, fetchQueue]);

  const unassignOrder = useCallback(async (orderId: string) => {
    await post(`/api/admin/queue/${orderId}/unassign`);
    await fetchQueue();
  }, [post, fetchQueue]);

  const holdOrder = useCallback(async (orderId: string, reason = 'awaiting-court-response') => {
    await post(`/api/admin/orders/${orderId}/hold`, { reason });
    await fetchQueue();
  }, [post, fetchQueue]);

  const releaseOrder = useCallback(async (orderId: string) => {
    await post(`/api/admin/orders/${orderId}/release`);
    await fetchQueue();
  }, [post, fetchQueue]);

  return {
    items,
    total,
    tabCounts,
    kpis,
    isLoading,
    error,
    refetch: fetchQueue,
    assignOrder,
    unassignOrder,
    holdOrder,
    releaseOrder,
  };
}
